import { createSlice, PayloadAction } from '@reduxjs/toolkit';

interface OptionalColumn {
  checked: boolean;
}

interface OptionalColumns {
  cost: OptionalColumn;
  price: OptionalColumn;
  markup: OptionalColumn;
  tax: OptionalColumn;
  discount: OptionalColumn;
  total: OptionalColumn;
  stockAvailabilityMain: OptionalColumn;
  stockAvailabilityVan: OptionalColumn;
  pickQuantityMain: OptionalColumn;
  pickQuantityVan: OptionalColumn;
}

// Column visibility interface for backend sync (similar to quotes)
interface ColumnVisibility {
  product: boolean;
  qty: boolean;
  price: boolean;
  tax: boolean;
  discount: boolean;
  total: boolean;
}

interface LineItem {
  id: string;
  product_uuid?: string; // UUID of the original product (for tracking product instances)
  name: string;
  unit_cost: number;
  unit_price: number;
  markup: number;
  tax: number;
  discount: number;
  description: string;
  total: number;
  item_type?: number; // 1 = Product, 0 = Service
  supplier_id?: string | null;
  stockAvailabilityMain: number;
  stockAvailabilityVan: number;
  pickQuantityMain: number;
  pickQuantityVan: number;
  reserved_quantity_main: number;
  reserved_quantity_van: number;
  quantity: number;
  isCustom?: boolean;
  is_locked?: boolean;
}

// Section types enum
export enum SectionType {
  PRODUCT_SERVICE = 'product_service',
  VARIATIONS = 'variations',
  EXPENSES = 'expenses',
}

interface Section {
  id: string;
  title: string;
  description: string;
  sectionType: SectionType;
  lineItems: LineItem[];
  optionalColumns: OptionalColumns;
  column_visibility?: ColumnVisibility; // For backend sync
  sections: Section[];
}

// Matches the backend's InventoryReservationService::shortfallsForJob shape - what a job asked
// to hold against what it actually holds, per product and stock pool (0 = main, 1 = van).
interface JobStockShortfall {
  product_id: string;
  inventory_type: number;
  wanted: number;
  held: number;
  short: number;
}

interface JobBuilderState {
  productServiceSections: Section[];
  variationSections: Section[];
  expenseSections: Section[];
  lastAddedLineItemId: string | null;
  stockShortfalls: JobStockShortfall[];
}

const initialState: JobBuilderState = {
  productServiceSections: [
    {
      id: `section_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      title: '',
      description: '',
      sectionType: SectionType.PRODUCT_SERVICE,
      lineItems: [],
      sections: [],
      optionalColumns: {
        cost: { checked: true },
        price: { checked: false },
        markup: { checked: false },
        tax: { checked: false },
        discount: { checked: false },
        total: { checked: false },
        stockAvailabilityMain: { checked: false },
        stockAvailabilityVan: { checked: false },
        pickQuantityMain: { checked: false },
        pickQuantityVan: { checked: false },
      },
      column_visibility: {
        product: true,
        qty: true,
        price: true,
        tax: false,
        discount: false,
        total: true,
      },
    },
  ],
  variationSections: [],
  expenseSections: [],
  lastAddedLineItemId: null,
  stockShortfalls: [],
};

// Helper function to find a section by ID in nested structure
const findSectionById = (
  sections: Section[],
  sectionId: string,
): Section | null => {
  for (const section of sections) {
    if (section.id === sectionId) {
      return section;
    }
    // Recursively search in nested sections
    const found = findSectionById(section.sections, sectionId);
    if (found) {
      return found;
    }
  }
  return null;
};

// Helper function to find a section by ID across all section types
const findSectionByIdInState = (
  state: JobBuilderState,
  sectionId: string,
): Section | null => {
  return (
    findSectionById(state.productServiceSections, sectionId) ||
    findSectionById(state.variationSections, sectionId) ||
    findSectionById(state.expenseSections, sectionId)
  );
};

// Helper function to remove a section by ID from nested structure
const removeSectionById = (
  sections: Section[],
  sectionId: string,
): Section[] => {
  return sections
    .filter((section) => section.id !== sectionId)
    .map((section) => ({
      ...section,
      sections: removeSectionById(section.sections, sectionId),
    }));
};

// Helper function to sanitize field values for job builder
const sanitizeFieldValue = (field: keyof LineItem, value: any): any => {
  const numericFields = [
    'unit_cost',
    'unit_price',
    'quantity',
    'markup',
    'discount',
    'tax',
    'stockAvailabilityMain',
    'stockAvailabilityVan',
    'pickQuantityMain',
    'pickQuantityVan',
    'total',
    'reserved_quantity_main',
    'reserved_quantity_van',
  ];

  if (!numericFields.includes(field)) {
    return value;
  }

  // Handle string inputs
  if (typeof value === 'string') {
    const parsed = parseFloat(value);
    return isNaN(parsed) ? 0 : parsed;
  }

  // Handle number inputs
  if (typeof value === 'number') {
    return isNaN(value) || !isFinite(value) ? 0 : value;
  }

  // Handle null/undefined
  return 0;
};

// Helper function to get sanitized calculation values
const getCalculationValues = (
  currentItem: LineItem,
  field: keyof LineItem,
  safeValue: any,
) => {
  const getSafeNumber = (itemValue: any) => {
    if (typeof itemValue === 'string') return parseFloat(itemValue) || 0;
    if (typeof itemValue === 'number') return itemValue || 0;
    return 0;
  };

  return {
    quantity:
      field === 'quantity' ? safeValue : getSafeNumber(currentItem.quantity),
    unitPrice:
      field === 'unit_price'
        ? safeValue
        : getSafeNumber(currentItem.unit_price),
    unitCost:
      field === 'unit_cost' ? safeValue : getSafeNumber(currentItem.unit_cost),
    markup: field === 'markup' ? safeValue : getSafeNumber(currentItem.markup),
    discount:
      field === 'discount' ? safeValue : getSafeNumber(currentItem.discount),
    tax: field === 'tax' ? safeValue : getSafeNumber(currentItem.tax),
  };
};

// Helper function to calculate total
const calculateTotal = (
  values: ReturnType<typeof getCalculationValues>,
): number => {
  const { quantity, unitPrice, markup, discount, tax } = values;

  const total = unitPrice * quantity;

  // Ensure result is valid
  return isNaN(total) || !isFinite(total) ? 0 : total;
};

const jobBuilderSlice = createSlice({
  name: 'jobBuilder',
  initialState,
  reducers: {
    addSection: (
      state,
      action: PayloadAction<{
        title: string;
        description?: string;
        sectionType?: SectionType;
        parentId?: string;
        id?: string;
        column_visibility?: OptionalColumns;
      }>,
    ) => {
      const {
        title,
        description = '',
        sectionType = SectionType.PRODUCT_SERVICE,
        parentId,
        id,
      } = action.payload;
      const newSection: Section = {
        id:
          id ||
          `section_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
        title,
        description,
        sectionType,
        lineItems: [],
        sections: [],
        optionalColumns: {
          cost: { checked: true },
          price: { checked: false },
          markup: { checked: false },
          tax: { checked: false },
          discount: { checked: false },
          total: { checked: false },
          stockAvailabilityMain: { checked: false },
          stockAvailabilityVan: { checked: false },
          pickQuantityMain: { checked: false },
          pickQuantityVan: { checked: false },
        },
        column_visibility: {
          product: true,
          qty: true,
          price: true,
          tax: true,
          discount: true,
          total: true,
        },
      };

      if (!parentId) {
        // Add to root level based on section type
        switch (sectionType) {
          case SectionType.PRODUCT_SERVICE:
            state.productServiceSections.push(newSection);
            break;
          case SectionType.VARIATIONS:
            state.variationSections.push(newSection);
            break;
          case SectionType.EXPENSES:
            state.expenseSections.push(newSection);
            break;
        }
      } else {
        // Add to specific parent section
        const parentSection = findSectionByIdInState(state, parentId);
        if (parentSection) {
          parentSection.sections.push(newSection);
        }
      }
    },

    removeSection: (state, action: PayloadAction<{ sectionId: string }>) => {
      const { sectionId } = action.payload;
      state.productServiceSections = removeSectionById(
        state.productServiceSections,
        sectionId,
      );
      state.variationSections = removeSectionById(
        state.variationSections,
        sectionId,
      );
      state.expenseSections = removeSectionById(
        state.expenseSections,
        sectionId,
      );
    },

    addLineItem: (
      state,
      action: PayloadAction<{ sectionId: string; lineItem: LineItem }>,
    ) => {
      const { sectionId, lineItem } = action.payload;

      // Ensure unit_cost and unit_price are properly set
      lineItem.unit_cost = (lineItem as any).cost || lineItem.unit_cost;
      lineItem.unit_price = (lineItem as any).price || lineItem.unit_price;

      // Calculate initial total for the line item
      const values = getCalculationValues(
        lineItem,
        'quantity',
        lineItem.quantity,
      );
      lineItem.total = calculateTotal(values);

      // A catalogue product commits its full quantity from main stock. Nothing has chosen a pick
      // quantity at this point, so this is the starting value; later edits stand, including zero.
      if (lineItem.product_uuid && !lineItem.reserved_quantity_main) {
        lineItem.reserved_quantity_main = lineItem.quantity ?? 0;
      }

      const section = findSectionByIdInState(state, sectionId);
      if (section) {
        section.lineItems.push(lineItem);
        // Track the newly added line item ID for auto-focus
        state.lastAddedLineItemId = lineItem.id;
      }
    },

    removeLineItem: (
      state,
      action: PayloadAction<{ sectionId: string; lineItemId: string }>,
    ) => {
      const { sectionId, lineItemId } = action.payload;
      const section = findSectionByIdInState(state, sectionId);
      if (section) {
        section.lineItems = section.lineItems.filter(
          (item) => item.id !== lineItemId,
        );
      }
    },

    updateLineItem: (
      state,
      action: PayloadAction<{
        sectionId: string;
        lineItemId: string;
        updates: Partial<LineItem>;
      }>,
    ) => {
      const { sectionId, lineItemId, updates } = action.payload;
      const section = findSectionByIdInState(state, sectionId);
      if (section) {
        const lineItemIndex = section.lineItems.findIndex(
          (item) => item.id === lineItemId,
        );
        if (lineItemIndex !== -1) {
          const merged = {
            ...section.lineItems[lineItemIndex],
            ...updates,
          };

          // Attaching a catalogue product to a line that had none commits its quantity, matching
          // what happens when the product is added as a new line. merged already carries the
          // line's existing values here (unlike addLineItem, where nothing has been chosen yet),
          // so a falsy check would overwrite a deliberately-set 0 - only an absent value defaults.
          if (
            updates.product_uuid &&
            !section.lineItems[lineItemIndex].product_uuid &&
            merged.reserved_quantity_main === undefined
          ) {
            merged.reserved_quantity_main = merged.quantity ?? 0;
          }

          section.lineItems[lineItemIndex] = merged;
        }
      }
    },

    updateLineItemField: (
      state,
      action: PayloadAction<{
        sectionId: string;
        lineItemId: string;
        field: keyof LineItem;
        value: any;
      }>,
    ) => {
      const { sectionId, lineItemId, field, value } = action.payload;
      const section = findSectionByIdInState(state, sectionId);
      if (section) {
        const lineItemIndex = section.lineItems.findIndex(
          (item) => item.id === lineItemId,
        );
        if (lineItemIndex !== -1) {
          const currentItem = section.lineItems[lineItemIndex];

          // Sanitize value for numeric fields
          const safeValue = sanitizeFieldValue(field, value);

          const updatedItem = {
            ...currentItem,
            [field]: safeValue,
          };

          // Auto-calculate total when relevant fields change
          if (
            [
              'quantity',
              'unit_price',
              'unit_cost',
              'markup',
              'discount',
              'tax',
            ].includes(field)
          ) {
            const values = getCalculationValues(currentItem, field, safeValue);
            updatedItem.total = calculateTotal(values);
          }

          section.lineItems[lineItemIndex] = updatedItem;
        }
      }
    },

    // Add action to update section title
    updateSectionTitle: (
      state,
      action: PayloadAction<{ sectionId: string; title: string }>,
    ) => {
      const { sectionId, title } = action.payload;
      const section = findSectionByIdInState(state, sectionId);
      if (section) {
        section.title = title;
      }
    },

    // Add action to update section description
    updateSectionDescription: (
      state,
      action: PayloadAction<{ sectionId: string; description: string }>,
    ) => {
      const { sectionId, description } = action.payload;
      const section = findSectionByIdInState(state, sectionId);
      if (section) {
        section.description = description;
      }
    },

    // Add action to update section details (title and/or description)
    updateSectionDetails: (
      state,
      action: PayloadAction<{
        sectionId: string;
        title?: string;
        description?: string;
      }>,
    ) => {
      const { sectionId, title, description } = action.payload;
      const section = findSectionByIdInState(state, sectionId);
      if (section) {
        if (title !== undefined) {
          section.title = title;
        }
        if (description !== undefined) {
          section.description = description;
        }
      }
    },

    // Add action to set sections from API data
    setSections: (
      state,
      action: PayloadAction<{
        productServiceSections?: Section[];
        variationSections?: Section[];
        expenseSections?: Section[];
        stockShortfalls?: JobStockShortfall[];
      }>,
    ) => {
      const {
        productServiceSections,
        variationSections,
        expenseSections,
        stockShortfalls,
      } = action.payload;

      // Helper function to recalculate totals for all line items in sections
      const recalculateLineItemTotals = (sections: Section[]): Section[] => {
        return sections.map((section) => ({
          ...section,
          lineItems: (section.lineItems || []).map(
            (lineItem) => {
              const values = getCalculationValues(
                lineItem,
                'quantity',
                lineItem.quantity,
              );
              return {
                ...lineItem,
                total: calculateTotal(values),
              };
            },
          ),
          sections: section.sections
            ? recalculateLineItemTotals(section.sections)
            : [],
        }));
      };

      if (productServiceSections) {
        state.productServiceSections = recalculateLineItemTotals(
          productServiceSections,
        );
      }
      if (variationSections) {
        state.variationSections = recalculateLineItemTotals(variationSections);
      }
      if (expenseSections) {
        state.expenseSections = recalculateLineItemTotals(expenseSections);
      }
      if (stockShortfalls) {
        state.stockShortfalls = stockShortfalls;
      }
    },

    // Add action to update column visibility
    updateColumnVisibility: (
      state,
      action: PayloadAction<{
        sectionId: string;
        column: keyof ColumnVisibility;
        visible: boolean;
      }>,
    ) => {
      const { sectionId, column, visible } = action.payload;
      const section = findSectionByIdInState(state, sectionId);
      if (section && section.column_visibility) {
        section.column_visibility[column] = visible;
      }
    },

    // Add action to reset sections to initial state
    resetSections: (state) => {
      state.productServiceSections = initialState.productServiceSections;
      state.variationSections = initialState.variationSections;
      state.expenseSections = initialState.expenseSections;
    },

    // Add action to clear last added line item ID (used after focus is applied)
    clearLastAddedLineItemId: (state) => {
      state.lastAddedLineItemId = null;
    },
  },
});

export const {
  addSection,
  addLineItem,
  updateLineItem,
  updateLineItemField,
  removeSection,
  removeLineItem,
  updateSectionTitle,
  updateSectionDescription,
  updateSectionDetails,
  updateColumnVisibility,
  setSections,
  resetSections,
  clearLastAddedLineItemId,
} = jobBuilderSlice.actions;
export default jobBuilderSlice.reducer;

// Selectors
export const selectProductServiceSections = (state: {
  jobBuilder: JobBuilderState;
}) => state.jobBuilder.productServiceSections;

export const selectVariationSections = (state: {
  jobBuilder: JobBuilderState;
}) => state.jobBuilder.variationSections;

export const selectExpenseSections = (state: { jobBuilder: JobBuilderState }) =>
  state.jobBuilder.expenseSections;

export const selectAllSections = (state: { jobBuilder: JobBuilderState }) => ({
  productServiceSections: state.jobBuilder.productServiceSections,
  variationSections: state.jobBuilder.variationSections,
  expenseSections: state.jobBuilder.expenseSections,
});

export const selectSectionById = (
  state: { jobBuilder: JobBuilderState },
  sectionId: string,
) => {
  return findSectionByIdInState(state.jobBuilder, sectionId);
};

export const selectSectionTitle = (
  state: { jobBuilder: JobBuilderState },
  sectionId: string,
) => {
  const section = findSectionByIdInState(state.jobBuilder, sectionId);
  return section?.title || '';
};

export const selectSectionDescription = (
  state: { jobBuilder: JobBuilderState },
  sectionId: string,
) => {
  const section = findSectionByIdInState(state.jobBuilder, sectionId);
  return section?.description || '';
};

export const selectLastAddedLineItemId = (state: { jobBuilder: JobBuilderState }) =>
  state.jobBuilder.lastAddedLineItemId;

export const selectStockShortfalls = (state: { jobBuilder: JobBuilderState }) =>
  state.jobBuilder.stockShortfalls;

// Legacy selector for backward compatibility (returns all sections combined)
export const selectSections = (state: { jobBuilder: JobBuilderState }) => [
  ...state.jobBuilder.productServiceSections,
  ...state.jobBuilder.variationSections,
  ...state.jobBuilder.expenseSections,
];

// Export types for use in components
export type {
  LineItem,
  Section,
  OptionalColumn,
  OptionalColumns,
  ColumnVisibility,
  JobStockShortfall,
};
